import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
// All hooks must be inside the component body
import { GoogleSheetsService, StandingRow } from '../services/GoogleSheetsService';
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TextField, Container, Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Autocomplete } from '@mui/material';
import { ClassOrderModal } from '../components/ClassOrderModal';

const sheetsService = new GoogleSheetsService();

const StandingsManagementPage: React.FC = () => {
  const [classOrder, setClassOrder] = useState<string[]>([]);
  const [classOrderModalOpen, setClassOrderModalOpen] = useState(false);
  const [classOrderLoading, setClassOrderLoading] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addRow, setAddRow] = useState<StandingRow>({ rowIndexId: 0, className: '', riderName: '', horseName: '', points: 0 });
  const [adding, setAdding] = useState(false);

  const handleAddChange = (field: keyof StandingRow, value: string | number) => {
    setAddRow({ ...addRow, [field]: value });
  };

  const handleAddSave = async () => {
    setAdding(true);
    try {
      // Ensure class is in column G
      await sheetsService.ensureClassInOrder(addRow.className);
      await sheetsService.addStandingRow(addRow);
      const records = await sheetsService.getAllStandings();
      setStandings(records);
      // Also update class order and sorted standings
      const order = await sheetsService.getClassOrder();
      setClassOrder(order);
      setSortedStandings(sortStandings(records, order));
      // Update dropdown options with new unique values
      const uniqueClasses = Array.from(new Set(records.map(r => r.className).filter(Boolean)));
      const uniqueRiders = Array.from(new Set(records.map(r => r.riderName).filter(Boolean)));
      const uniqueHorses = Array.from(new Set(records.map(r => r.horseName).filter(Boolean)));
      setClassOptions(uniqueClasses);
      setRiderOptions(uniqueRiders);
      setHorseOptions(uniqueHorses);
      setAddDialogOpen(false);
      setAddRow({ rowIndexId: 0, className: '', riderName: '', horseName: '', points: 0 });
    } catch (err) {
      setError('Failed to add standing.');
    } finally {
      setAdding(false);
    }
  };
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    if (editRow) {
      setDeleting(true);
      try {
        await sheetsService.deleteRowByIndex('Standings', editRow.rowIndexId);
        // Refresh standings
        const records = await sheetsService.getAllStandings();
        setStandings(records);
        setSortedStandings(sortStandings(records, classOrder));
        setEditIndex(null);
        setEditRow(null);
        setDeleteConfirmOpen(false);
      } catch (err) {
        setError('Failed to delete row.');
      } finally {
        setDeleting(false);
      }
    }
  };
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Refresh handler
  const handleRefresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const records = await sheetsService.getAllStandings();
      const order = await sheetsService.getClassOrder();
      setStandings(records);
      setClassOrder(order);
      setSortedStandings(sortStandings(records, order));
    } catch (err) {
      setError('Failed to refresh standings.');
    } finally {
      setLoading(false);
    }
  };
  const [standings, setStandings] = useState<StandingRow[]>([]);
  const [sortedStandings, setSortedStandings] = useState<StandingRow[]>([]);

  // Helper to sort standings by class order from G, then by points desc
  function sortStandings(standings: StandingRow[], classOrder: string[]): StandingRow[] {
    const classIndex = (cls: string) => {
      const idx = classOrder.indexOf(cls);
      return idx === -1 ? 9999 : idx;
    };
    return [...standings].sort((a, b) => {
      const classDiff = classIndex(a.className) - classIndex(b.className);
      if (classDiff !== 0) return classDiff;
      return b.points - a.points;
    });
  }
  const [classOptions, setClassOptions] = useState<string[]>([]);
  const [riderOptions, setRiderOptions] = useState<string[]>([]);
  const [horseOptions, setHorseOptions] = useState<string[]>([]);
  const [editIndex, setEditIndex] = useState<number | null>(null);
  const [editRow, setEditRow] = useState<StandingRow | null>(null);

  // Class order modal state
  // (state already declared above)
  const handleOpenClassOrderModal = async () => {
    setClassOrderLoading(true);
    setClassOrderModalOpen(true);
    try {
      const order = await sheetsService.getClassOrder();
      setClassOrder(order);
    } finally {
      setClassOrderLoading(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const records = await sheetsService.getAllStandings();
        const order = await sheetsService.getClassOrder();
        setStandings(records);
        setClassOrder(order);
        setSortedStandings(sortStandings(records, order));
        // Extract unique class, rider, and horse names
        const uniqueClasses = Array.from(new Set(records.map(r => r.className).filter(Boolean)));
        const uniqueRiders = Array.from(new Set(records.map(r => r.riderName).filter(Boolean)));
        const uniqueHorses = Array.from(new Set(records.map(r => r.horseName).filter(Boolean)));
        setClassOptions(uniqueClasses);
        setRiderOptions(uniqueRiders);
        setHorseOptions(uniqueHorses);
      } catch (err) {
        setError('Failed to load standings.');
      }
    };
    fetchData();
  }, []);

  const handleEdit = (index: number) => {
    setEditIndex(index);
    setEditRow({ ...standings[index] });
  };

  const handleCancel = () => {
    setEditIndex(null);
    setEditRow(null);
  };

  const handleChange = (field: keyof StandingRow, value: string | number) => {
    if (editRow) {
      setEditRow({ ...editRow, [field]: value });
    }
  };

  const handleSave = async () => {
    if (editIndex !== null && editRow) {
      // Ensure class is in column G (class order)
      await sheetsService.ensureClassInOrder(editRow.className);
      // Update the row in Google Sheets
      await sheetsService.updateStandingRowByIndex(editRow.rowIndexId, editRow);
      // Reload all standings and class order from backend
      try {
        const records = await sheetsService.getAllStandings();
        const order = await sheetsService.getClassOrder();
        setStandings(records);
        setClassOrder(order);
        setSortedStandings(sortStandings(records, order));
      } catch (err) {
        setError('Failed to reload standings after update.');
      }
      handleCancel();
    }
  };

  const navigate = useNavigate();
  return (
    <Container maxWidth="md">
      <Button variant="outlined" onClick={() => navigate(-1)} sx={{ mt: 2, mb: 2 }}>
        Back
      </Button>
      <Paper elevation={3} sx={{ padding: 4, marginTop: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" component="h2">Standings Management</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="contained" color="primary" onClick={() => setAddDialogOpen(true)} disabled={loading}>
              Add Standing
            </Button>
            <Button variant="outlined" onClick={handleRefresh} disabled={loading}>
              Refresh
            </Button>
            <Button variant="outlined" color="secondary" onClick={handleOpenClassOrderModal} disabled={loading}>
              Edit Class Order
            </Button>
          </Box>
        </Box>
                {/* Add Standing Dialog */}
                <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
                  <DialogTitle>Add Standing</DialogTitle>
                  <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                      <Autocomplete
                        freeSolo
                        options={classOptions}
                        value={addRow.className}
                        onInputChange={(_, newValue) => handleAddChange('className', newValue)}
                        renderInput={(params) => (
                          <TextField {...params} label="Class" fullWidth />
                        )}
                      />
                      <Autocomplete
                        freeSolo
                        options={riderOptions}
                        value={addRow.riderName}
                        onInputChange={(_, newValue) => handleAddChange('riderName', newValue)}
                        renderInput={(params) => (
                          <TextField {...params} label="Rider Name" fullWidth />
                        )}
                      />
                      <Autocomplete
                        freeSolo
                        options={horseOptions}
                        value={addRow.horseName}
                        onInputChange={(_, newValue) => handleAddChange('horseName', newValue)}
                        renderInput={(params) => (
                          <TextField {...params} label="Horse Name" fullWidth />
                        )}
                      />
                      <TextField
                        label="Points"
                        type="number"
                        value={addRow.points}
                        onChange={e => handleAddChange('points', Number(e.target.value))}
                        fullWidth
                      />
                    </Box>
                  </DialogContent>
                  <DialogActions>
                    <Button onClick={() => setAddDialogOpen(false)} color="secondary" variant="outlined" disabled={adding}>Cancel</Button>
                    <Button onClick={handleAddSave} color="primary" variant="contained" disabled={adding}>Save</Button>
                  </DialogActions>
                </Dialog>
        <Typography variant="body2" sx={{ mb: 2 }}>Manage and edit standings below. Click a row to edit.</Typography>
        {error && <Typography color="error">{error}</Typography>}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Typography>Loading...</Typography>
          </Box>
        )}
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ border: '1px solid #e0e0e0', padding: 8, background: '#4d4d4dff', width: 160 }}>Class</th>
                <th style={{ border: '1px solid #e0e0e0', padding: 8, background: '#4d4d4dff' }}>Rider Name</th>
                <th style={{ border: '1px solid #e0e0e0', padding: 8, background: '#4d4d4dff' }}>Horse Name</th>
                <th style={{ border: '1px solid #e0e0e0', padding: 8, background: '#4d4d4dff' }}>Points</th>
              </tr>
            </thead>
            <tbody>
              {sortedStandings.map((row, idx) => (
                <React.Fragment key={row.rowIndexId}>
                  <tr
                    style={{ cursor: editIndex === null ? 'pointer' : 'default' }}
                    onClick={editIndex === null ? () => {
                      setEditIndex(idx);
                      setEditRow({ ...row });
                    } : undefined}
                  >
                    {editIndex === idx ? (
                      <>
                        <td style={{ border: '1px solid #e0e0e0', padding: 8, width: 160 }}><TextField value={editRow?.className || ''} onChange={e => handleChange('className', e.target.value)} fullWidth /></td>
                        <td style={{ border: '1px solid #e0e0e0', padding: 8 }}><TextField value={editRow?.riderName || ''} onChange={e => handleChange('riderName', e.target.value)} fullWidth /></td>
                        <td style={{ border: '1px solid #e0e0e0', padding: 8 }}><TextField value={editRow?.horseName || ''} onChange={e => handleChange('horseName', e.target.value)} fullWidth /></td>
                        <td style={{ border: '1px solid #e0e0e0', padding: 8 }}><TextField type="number" value={editRow?.points || 0} onChange={e => handleChange('points', Number(e.target.value))} fullWidth /></td>
                      </>
                    ) : (
                      <>
                        <td style={{ border: '1px solid #e0e0e0', padding: 8, width: 160 }}>{row.className}</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: 8 }}>{row.riderName}</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: 8 }}>{row.horseName}</td>
                        <td style={{ border: '1px solid #e0e0e0', padding: 8 }}>{row.points}</td>
                      </>
                    )}
                  </tr>
                  {editIndex === idx && (
                    <>
                      <tr>
                        <td colSpan={4} style={{ textAlign: 'center', padding: '16px 0' }}>
                          <Button onClick={handleSave} color="primary" variant="contained" sx={{ mr: 2 }}>Save</Button>
                          <Button onClick={handleCancel} color="secondary" variant="outlined" sx={{ mr: 2 }}>Cancel</Button>
                          <Button onClick={() => setDeleteConfirmOpen(true)} color="error" variant="outlined" disabled={deleting}>Delete</Button>
                        </td>
                      </tr>
                      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
                        <DialogTitle>Delete Standing</DialogTitle>
                        <DialogContent>
                          <Typography>Are you sure you want to delete this standing? This action cannot be undone.</Typography>
                        </DialogContent>
                        <DialogActions>
                          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>Cancel</Button>
                          <Button color="error" variant="contained" onClick={handleDelete} disabled={deleting}>Delete</Button>
                        </DialogActions>
                      </Dialog>
                    </>
                  )}
                </React.Fragment>
              ))}
                  {/* Class Order Modal (rendered once at root, not in map) */}
                  <ClassOrderModal
                    open={classOrderModalOpen}
                    loading={classOrderLoading}
                    classOrder={classOrder}
                    onClose={() => setClassOrderModalOpen(false)}
                    onSave={async (newOrder) => {
                      setClassOrderLoading(true);
                      await sheetsService.setClassOrder(newOrder);
                      setClassOrder(newOrder);
                      // Re-sort standings after save
                      setSortedStandings(sortStandings(standings, newOrder));
                      setClassOrderModalOpen(false);
                      setClassOrderLoading(false);
                    }}
                  />
            </tbody>
          </table>
          {standings.length === 0 && (
            <Typography variant="body2" sx={{ mt: 2 }}>No standings found.</Typography>
          )}
        </Box>
      </Paper>
    </Container>
  );
};

export default StandingsManagementPage;
